import { newId } from '../../core/ids';
import { getFeatureFlags, loadFeatureFlags } from '../../core/featureFlags';
import { SERVER_PROVIDER_ID } from '../../core/providers';
import type { ProviderAdapter } from '../../core/providers/types';
import { conversationRepo, messageRepo, presetRepo, providerRepo } from '../../core/repositories';
import { resolveRoleConversation } from '../../core/roleModel';
import { applyRoleContextBudget } from '../../core/roleContext';
import type { ChatMessage, ModelSettings, Preset, ToolSpec } from '../../core/types';
import { parseVoiceDirection } from '../../../server/src/modules/tts/voice-direction';
import type { VoiceDirection } from '../../../server/src/modules/tts/voice-direction';
import { completedToolCalls, mergeToolCallDelta } from '../automation/richOutput';
import type { StreamToolCall } from '../automation/richOutput';
import { modelGateway } from '../modelGateway';
import { readSecret } from '../providers/secretStore';
import type { VoiceMode } from './contracts';
import type { LocalRoleVoice, TtsSelection } from './adapter';
import type { TtsRequest } from '../../core/tts';
import { loadTtsCatalog } from './catalog';
import { roleVoiceError, roleVoiceStore } from './roleVoiceStore';
import { MimoError } from './contracts';
import type { AutomationPrompts } from '../../../server/src/modules/operations/automationPrompts';

const SERVER_PROVIDER_CONFIG = { id: SERVER_PROVIDER_ID, kind: 'custom' as const, purpose: 'chat' as const, name: '官方模型', baseUrl: '', apiKeyRef: '', enabled: true };

/** Manual playback and continuous reading use the role bound to the actual message. */
export async function loadMessageRoleVoice(request: TtsRequest, selection: TtsSelection): Promise<LocalRoleVoice | undefined> {
  if (!request.messageId) return undefined;
  const message = await messageRepo.getMessage(request.messageId);
  request.signal?.throwIfAborted();
  if (!message) return undefined;
  if (message.backgroundJob?.userId && message.backgroundJob.userId !== selection.userId) throw new MimoError('请先登录原账号读取角色音色');
  const conversation = await conversationRepo.getConversation(message.conversationId);
  const preset = conversation?.presetId ? await presetRepo.getPreset(conversation.presetId) : undefined;
  if (preset?.ttsProviderId !== SERVER_PROVIDER_ID || !preset.ttsModel) return undefined;
  const selected = (await loadTtsCatalog()).find(row => row.id === preset.ttsModel && row.available);
  if (!selected) throw new MimoError('角色语音模型不可用');
  request.signal?.throwIfAborted();
  const profile = selected.mode === 'preset' ? undefined : await roleVoiceStore.read(selection.userId, preset.id);
  request.signal?.throwIfAborted();
  const error = roleVoiceError(selected.mode, profile, preset.ttsAutoVoiceDesign === true);
  if (error) throw new MimoError(error);
  return {
    mode: selected.mode,
    description: (selected.mode === 'design' ? profile?.designDescription : profile?.cloneDescription) ?? '',
    ...(profile?.reference ? { reference: profile.reference } : {}),
    autoVoiceDesign: preset.ttsAutoVoiceDesign === true,
    preset,
  };
}

export interface RoleVoiceDesignInput {
  preset: Preset;
  mode: Exclude<VoiceMode, 'preset'>;
  text: string;
  referenceWords: string;
  autoVoiceDesign: boolean;
  messageId?: string;
  signal?: AbortSignal;
}

export interface VoiceDesignModel {
  adapter: ProviderAdapter;
  model: string;
  settings: ModelSettings;
  context: readonly ChatMessage[];
}

export function voiceDesignTool(fullDesign: boolean, prompts: AutomationPrompts = getFeatureFlags().automationPrompts): ToolSpec {
  const properties = {
    ...(fullDesign ? { gender: { type: 'string' }, age: { type: 'string' }, texture: { type: 'string' } } : {}),
    emotion: { type: 'string' }, undertone: { type: 'string' }, pace: { type: 'string' },
  };
  return {
    name: 'zhixin_design_voice',
    description: fullDesign ? prompts.voiceDesignFull : prompts.voiceDesignDelivery,
    inputSchema: { type: 'object', properties, required: Object.keys(properties), additionalProperties: false },
  };
}

async function resolveVoiceDesignModel(input: RoleVoiceDesignInput): Promise<VoiceDesignModel> {
  const message = input.messageId ? await messageRepo.getMessage(input.messageId) : undefined;
  const previous = message ? await conversationRepo.getConversation(message.conversationId)
    : (await conversationRepo.listConversations()).find(row => row.presetId === input.preset.id && row.modelKind !== 'image');
  const providers = await providerRepo.listProviders();
  const conversation = resolveRoleConversation(input.preset, previous, providers);
  // A recovered reply retains its actual generation model, including for legacy cards.
  const receipt = message?.backgroundJob;
  const providerId = receipt?.provider.id ?? conversation?.providerId;
  const model = receipt?.model ?? conversation?.model;
  const settings = receipt?.settings ?? conversation?.settings;
  if (!providerId || !model?.trim() || !settings) throw new MimoError('请先为角色配置可用的文字模型，以设计语音情绪和音色');
  const provider = providerId === SERVER_PROVIDER_ID ? SERVER_PROVIDER_CONFIG : providers.find(row => row.id === providerId);
  if (!provider?.enabled || provider.purpose === 'tts' || provider.purpose === 'image') throw new MimoError('角色音色设计所用的文字模型提供商已停用或删除');
  const apiKey = provider.apiKeyRef.trim() ? readSecret(provider.apiKeyRef) : null;
  let history = previous ? await messageRepo.listMessages(previous.id) : [];
  if (message) {
    const index = history.findIndex(row => row.id === message.id);
    if (index >= 0) history = history.slice(0, index + 1);
  }
  return {
    adapter: modelGateway.createChatAdapter(apiKey ? { ...provider, apiKey } : provider),
    model: providerId === SERVER_PROVIDER_ID ? [...model.matchAll(/\(([^()]+)\)/g)].at(-1)?.[1] ?? model : model,
    settings,
    context: history.filter(row => row.status === 'done').slice(-30),
  };
}

/** A separate model call uses the same authenticated, metered gateway as role chat. */
export async function createRoleVoiceDirection(input: RoleVoiceDesignInput, resolved?: VoiceDesignModel): Promise<VoiceDirection> {
  input.signal?.throwIfAborted();
  const target = resolved ?? await resolveVoiceDesignModel(input);
  const { automationPrompts: prompts } = await loadFeatureFlags(true);
  input.signal?.throwIfAborted();
  const fullDesign = input.autoVoiceDesign && input.mode === 'design';
  const tool = voiceDesignTool(fullDesign, prompts);
  const instruction = [
    prompts.voiceDesignProtocol,
    fullDesign ? prompts.voiceDesignFull : prompts.voiceDesignDelivery,
  ].filter(Boolean).join('\n');
  const data = {
    role: { ...input.preset, avatar: undefined },
    referenceWords: input.referenceWords,
    mode: input.mode,
    autoVoiceDesign: input.autoVoiceDesign,
    modelSystemPrompt: target.settings.systemPrompt ?? '',
    conversation: target.context.map(row => ({ role: row.role, content: row.content,
      ...(row.attachments?.length ? { attachments: row.attachments.map(item => ({ kind: item.kind, name: item.name })) } : {}) })),
    textToSpeak: input.text,
  };
  const message = (role: 'system' | 'user', content: string): ChatMessage => ({
    id: newId(), conversationId: 'voice-design', role, content, status: 'done', createdAt: Date.now(),
  });
  let text = '';
  const calls = new Map<number, StreamToolCall>();
  const makeRequest = () => ({
    model: target.model,
    settings: { ...target.settings, systemPrompt: undefined },
    messages: [message('system', instruction), message('user', JSON.stringify(data))],
    tools: [tool],
    ...(input.signal ? { signal: input.signal } : {}),
  });
  const budgetPreset = { modelConfig: input.preset.modelConfig ?? { providerId: target.adapter.id, model: target.model, settings: target.settings } };
  let request = makeRequest();
  // Keep every character field and the spoken text; only old conversation data may be removed.
  for (;;) {
    try { request = applyRoleContextBudget(request, budgetPreset); break; }
    catch {
      if (data.conversation.length === 0) throw new MimoError('角色音色设计资料和当前文本超过模型最大上下文，请提高角色模型最大上下文或缩短角色资料');
      data.conversation.shift();
      request = makeRequest();
    }
  }
  for await (const chunk of target.adapter.chat(request)) {
    input.signal?.throwIfAborted();
    if (chunk.type === 'text') text += chunk.delta ?? '';
    else if (chunk.type === 'tool_call') mergeToolCallDelta(calls, chunk.delta ?? '');
    else if (chunk.type === 'error') throw new Error(chunk.error ?? '角色音色设计失败');
    else if (chunk.type === 'done') break;
    if (text.length > 16000) throw new MimoError('角色音色设计结果过长，请重试');
  }
  input.signal?.throwIfAborted();
  try {
    const call = completedToolCalls(calls).find(item => item.name === tool.name);
    const value: unknown = call?.arguments ?? JSON.parse(text.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, ''));
    return parseVoiceDirection(value, fullDesign);
  } catch { throw new MimoError('角色音色设计未返回有效的声音参数，请重试'); }
}
